import { formatDuration } from '@airreading/common';
import { SessionStatus } from '@airreading/common/data';
import { useEffect } from 'react';
import { Temporal } from 'temporal-polyfill';

import type { ConcreteEvent } from './session-schedule-calendar-types';

/** How often to refresh the “next class in …” label while the calendar is mounted. */
const TOOLTIP_UPDATE_INTERVAL_MS = 60_000;

const TOOLTIP_SELECTORS = {
  legacy: {
    indicators: ['.rbc-current-time-indicator'],
    observeRoot: '.rbc-time-content',
  },
  'schedule-x': {
    // Day view uses per-column indicator; week view uses full-week bar.
    indicators: ['.sx__current-time-indicator', '.sx__current-time-indicator-full-week'],
    observeRoot: '.session-schedule-calendar-schedule-x',
  },
} as const;

export type SessionNextClassTooltipVariant = keyof typeof TOOLTIP_SELECTORS;

/**
 * Label for the “now” line tooltip (current / next class today in the coach timezone).
 * Exported for tests; pure function over {@link ConcreteEvent} data from {@link eventsFromSessions}.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function getNextClassTooltipLabel(events: ConcreteEvent[], timeZone: string): string {
  const now = Temporal.Now.instant();
  const today = Temporal.Now.zonedDateTimeISO(timeZone).toPlainDate();

  const current = events.find(
    (e) => Temporal.PlainDate.compare(e.startDate, today) === 0 && e.eventInterval.contains(now),
  );

  if (current) {
    return current.status !== SessionStatus.cancelled ? 'Class in progress' : 'Class cancelled';
  }

  const upcomingToday = events
    .filter(
      (e) =>
        Temporal.PlainDate.compare(e.startDate, today) === 0 &&
        Temporal.Instant.compare(e.eventInterval.start, now) > 0,
    )
    .toSorted((a, b) => Temporal.Instant.compare(a.eventInterval.start, b.eventInterval.start));

  const [next] = upcomingToday;

  if (!next) {
    return 'No more classes today';
  }
  if (next.status === SessionStatus.cancelled) {
    return 'Next class cancelled';
  }

  const duration = next.eventInterval.start.since(now, { largestUnit: 'hours', smallestUnit: 'minutes' });
  return duration.total({ unit: 'minutes' }) <= 0 ? 'Class starting now' : `Next class in ${formatDuration(duration)}`;
}

function findTimeIndicators(variant: SessionNextClassTooltipVariant): Element[] {
  const { indicators, observeRoot } = TOOLTIP_SELECTORS[variant];
  const root = document.querySelector(observeRoot);
  if (!root) {
    return [];
  }
  return indicators.flatMap((selector) => Array.from(root.querySelectorAll(selector)));
}

function applyTooltipLabel(events: ConcreteEvent[], timeZone: string, variant: SessionNextClassTooltipVariant): void {
  const label = getNextClassTooltipLabel(events, timeZone);
  for (const indicator of findTimeIndicators(variant)) {
    indicator.setAttribute('data-tooltip', label);
  }
}

function clearTooltipLabel(variant: SessionNextClassTooltipVariant): void {
  for (const indicator of findTimeIndicators(variant)) {
    indicator.removeAttribute('data-tooltip');
  }
}

/**
 * Sets `data-tooltip` on the calendar library’s “current time” indicator (RBC or Schedule-X).
 * Renders nothing; styling is in calendar.css / schedule-x-calendar.css via `attr(data-tooltip)`.
 */
export function SessionNextClassTooltip({
  events,
  timeZone,
  variant = 'legacy',
}: {
  events: ConcreteEvent[];
  timeZone: string;
  variant?: SessionNextClassTooltipVariant;
}) {
  useEffect(() => {
    applyTooltipLabel(events, timeZone, variant);

    const interval = setInterval(() => {
      applyTooltipLabel(events, timeZone, variant);
    }, TOOLTIP_UPDATE_INTERVAL_MS);

    const observer = new MutationObserver(() => {
      applyTooltipLabel(events, timeZone, variant);
    });

    const root = document.querySelector(TOOLTIP_SELECTORS[variant].observeRoot);
    if (root) {
      observer.observe(root, { childList: true, subtree: true });
    }

    return () => {
      clearInterval(interval);
      observer.disconnect();
      clearTooltipLabel(variant);
    };
  }, [events, timeZone, variant]);

  return null;
}
